import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { articles } from '../../mocks/articles';

const categories = ['All', 'Cybersecurity', 'Cloud', 'Managed IT', 'Consulting', 'Trends'];

export default function Insights() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All' ? articles : articles.filter((a) => a.category === activeCategory);
  const featured = filtered.find((a) => a.featured) || filtered[0];
  const rest = filtered.filter((a) => a.id !== featured?.id);

  return (
    <div className="min-h-screen bg-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-6 bg-white border-b border-black/[0.06]">
        <div className="mx-auto px-6 lg:px-10 xl:px-16 text-center">
          <span className="eyebrow">Knowledge Hub</span>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-page-ink mb-3 tracking-[-0.03em]">Insights &amp; Resources</h1>
          <p className="text-page-muted text-base max-w-2xl mx-auto leading-relaxed font-light">
            Expert perspectives on cybersecurity, cloud, managed IT, and the trends shaping Nigeria's digital future.
          </p>
        </div>
      </section>

      {/* Categories */}
      <div className="sticky top-[64px] lg:top-[72px] z-30 bg-white/90 backdrop-blur-md border-b border-black/[0.06]">
        <div className="mx-auto px-6 lg:px-10 xl:px-16 py-4 flex items-center space-x-2 overflow-x-auto scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                activeCategory === cat
                  ? 'bg-[#c97a2b] text-white'
                  : 'bg-black/[0.05] text-page-muted hover:bg-black/[0.08] hover:text-page-ink'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <section className="py-8 mx-auto px-6 lg:px-10 xl:px-16">
        {/* Featured */}
        {featured && (
          <div className="mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch bg-white rounded-2xl overflow-hidden border border-black/[0.08] hover:border-[#c97a2b]/30 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <Link
                to={`/insights/${featured.slug}`}
                className="h-72 lg:h-full min-h-[300px] overflow-hidden block cursor-pointer"
              >
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="p-8 lg:p-12">
                <span className="inline-block px-3 py-1 bg-[#c97a2b]/10 border border-[#c97a2b]/25 text-[#c97a2b] text-xs font-bold rounded-md mb-5 uppercase tracking-wide">
                  {featured.category}
                </span>
                <Link to={`/insights/${featured.slug}`} className="block cursor-pointer">
                  <h2 className="text-2xl lg:text-3xl font-extrabold text-page-ink mb-4 leading-tight tracking-[-0.02em] hover:text-[#c97a2b] transition-colors">
                    {featured.title}
                  </h2>
                </Link>
                <p className="text-page-muted leading-relaxed mb-6 text-sm">{featured.excerpt}</p>
                <div className="flex items-center space-x-4 mb-7">
                  <div>
                    <div className="font-semibold text-page-ink text-sm">{featured.author}</div>
                    <div className="text-page-faint text-xs">{featured.date} · {featured.readTime}</div>
                  </div>
                </div>
                <Link
                  to={`/insights/${featured.slug}`}
                  className="btn-primary text-sm"
                >
                  Read Article <i className="ri-arrow-right-line text-xs" />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Article Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((article) => (
            <Link
              key={article.id}
              to={`/insights/${article.slug}`}
              className="group bg-white rounded-xl border border-black/[0.08] overflow-hidden hover:border-[#c97a2b]/30 hover:-translate-y-1 transition-all duration-300 cursor-pointer block shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <span className="inline-block px-2.5 py-1 bg-[#c97a2b]/10 border border-[#c97a2b]/20 text-[#c97a2b] text-xs font-bold rounded-md mb-4 uppercase tracking-wide">
                  {article.category}
                </span>
                <h3 className="font-bold text-page-ink text-base leading-tight mb-3 group-hover:text-[#c97a2b] transition-colors">
                  {article.title}
                </h3>
                <p className="text-page-muted text-sm leading-relaxed mb-5 line-clamp-3">{article.excerpt}</p>
                <div className="flex items-center justify-between pt-4 border-t border-black/[0.06]">
                  <div>
                    <div className="text-page-muted text-xs font-semibold">{article.author}</div>
                    <div className="text-page-faint text-xs">{article.date}</div>
                  </div>
                  <div className="flex items-center gap-1 text-[#c97a2b] text-xs font-semibold whitespace-nowrap">
                    Read Article
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
